import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Box,
    Button,
    Image,
    Modal,
    Typography,
    TextField
} from '@iho-design-system/Loader';

import EmptySVG from '../../../assets/images/icons/empty.svg';
import LoadingCustomSVG from '../../../assets/images/icons/loading-custom.svg';

import { useFinance } from '../../../context/FinanceContext';

import post from '../../../api/post';
import getAPI from '../../../api/get';

import UnsettledReservationsTab from './UnsettledReservationsTab';
import PartialBalanceTab from './PartialBalanceTab';

const List = ({ props, alertProps, insufficientStatusProps, reloadFinanceProps }) => {
    const [creditPayments, setCreditPayments] = useState('loading');
    const [partialBalance, setPartialBalance] = useState('loading');
    const [balance, setBalance] = useState(0);
    const [selectedCreditPayment, setSelectedCreditPayment] = useState({});
    const [settlementAmount, setSettlementAmount] = useState(0);
    const [insufficientStatus, setInsufficientStatus] = useState(false);
    const [insufficientAmount, setInsufficientAmount] = useState(0);
    const [credit, setCredit] = useState('1,000,000');

    const { activeTab, setActiveTab } = useFinance();

    const buildSearchQuery = useCallback((includeServiceType = false) => {
        const query = new URLSearchParams();

        if (includeServiceType) {
            query.set('ServiceType', props.serviceType || 1);
        }
        if (props.startDate) query.set('StartDate', props.startDate);
        if (props.endDate) query.set('EndDate', props.endDate);

        return `?${query.toString()}`;
    }, [props.endDate, props.serviceType, props.startDate]);

    const fetchCreditPayments = useCallback(async () => {
        setCreditPayments('loading');
        const params = {
            ctrl: 'Financial',
            method: 'GetCreditPayments',
            query: buildSearchQuery(true)
        };
        const result = await getAPI(params);
        setCreditPayments(result.status === 200 && result.data.length ? result.data : 'empty');
    }, [buildSearchQuery]);

    const fetchPartialBalance = useCallback(async () => {
        setPartialBalance('loading');
        const params = {
            ctrl: 'Financial',
            method: 'GetPartialPayedReserves',
            query: buildSearchQuery()
        };
        const result = await getAPI(params);
        setPartialBalance(result.status === 200 && result.data.length ? result.data : 'empty');
    }, [buildSearchQuery]);

    useEffect(() => {
        if (props.balance.status === 200) {
            setBalance(props.balance.data);
        }
        if (activeTab === 1) fetchCreditPayments();
        if (activeTab === 2) fetchPartialBalance();
    }, [activeTab, fetchCreditPayments, fetchPartialBalance, props.balance]);

    const chargeWallet = async () => {
        const amount = credit.replaceAll(',', '');
        const params = {
            ctrl: 'payment/Wallet',
            method: 'GetToken/44',
            query: amount * 10
        };
        const res = await getAPI(params);
        if (+amount > 4999) {
            window.location.replace(`/api/mvc/payment/Wallet/GotoParsianIPG/${res.data.orderId}`);
        }
    };

    const calculateSelection = useCallback(() => {
        return Object.entries(selectedCreditPayment).reduce(
            (acc, [key, value]) => {
                const [checked, price] = value.split(',');
                if (checked === 'true') {
                    acc.ids.push(key);
                    acc.sum += +price;
                }
                return acc;
            },
            { ids: [], sum: 0 }
        );
    }, [selectedCreditPayment]);

    const creditSettlement = async () => {
        closeModal('confirmCreditSettlementModal');

        const { ids, sum } = calculateSelection();

        if (sum > balance.wallet.walletBalance) {
            setInsufficientStatus(true);
            insufficientStatusProps(true);
            setInsufficientAmount(sum - balance.wallet.walletBalance);
            return;
        }
        

        setInsufficientStatus(false);
        insufficientStatusProps(false);
        setInsufficientAmount(0);

        const body = {
            items: ids,
            ...(activeTab === 1 && { serviceType: +props.serviceType })
        };

        const params = {
            ctrl: 'Financial',
            method: activeTab === 1 ? 'CreditSettlemen' : 'PartialPayedSettlement',
            query: '',
            body
        };

        const result = await post(params);

        if (result.status === 200) {
            alertProps(['success', 'عملیات با موفقیت انجام شد']);
            reloadFinanceProps(Math.floor(Math.random() * 100));
            if(activeTab === 1){
                const result = creditPayments.filter(item => !Object.keys(selectedCreditPayment).includes(item.Id.toString()));
                setCreditPayments(result)
            }
            if(activeTab === 2){
                const result = partialBalance.filter(item => !Object.keys(selectedCreditPayment).includes(item.Id.toString()));
                setPartialBalance(result)
            }
            setSelectedCreditPayment({});
            
        } else {
            alertProps(['danger', result.data.Message]);
        }

        document.querySelectorAll('input[type=checkbox]').forEach((i) => (i.checked = false));
    };

    const openModal = (modal) => {
        if (modal === 'confirmCreditSettlementModal') {
            const { sum } = calculateSelection();
            setSettlementAmount(sum);
            if (!sum) return;
        }
        document.querySelector(`.${modal}`).classList.remove('d-none');
    };

    const closeModal = (modal) => {
        document.querySelector(`.${modal}`).classList.add('d-none');
    };

    const TabsHeader = useMemo(
        () => (
            <Box className="d-flex justify-content-center mb-md">
                {[1, 2].map((tab) => (
                    <Box
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className="px-lg py-sm cursor-pointer"
                        ms={{
                            borderBottom:
                                activeTab === tab
                                    ? '2px solid var(--blue-medium)'
                                    : '2px solid transparent',
                            color:
                                activeTab === tab
                                    ? 'var(--blue-medium)'
                                    : 'var(--gray-dark)'
                        }}>
                        <Typography>
                            {tab === 1
                                ? 'رزروهای تسویه نشده'
                                : 'رزروهای اصلاح شده دارای مانده'}
                        </Typography>
                    </Box>
                ))}
            </Box>
        ),
        [activeTab, setActiveTab]
    );

    return (
        <>
            {TabsHeader}

            {activeTab === 1 && creditPayments === 'loading' && (
                <Image className="d-block mx-auto" src={LoadingCustomSVG} ms={{ width: 40, height: 40 }} />
            )}

            {activeTab === 1 && creditPayments === 'empty' && (
                <Box>
                    <Image src={EmptySVG} className="mx-auto" ms={{ width: 120, height: 120 }} />
                    <Typography className="text-center">گزارشات مالی شما خالی است</Typography>
                </Box>
            )}

            {activeTab === 1 && Array.isArray(creditPayments) && (
                <UnsettledReservationsTab
                    creditPayments={creditPayments}
                    selectedCreditPayment={selectedCreditPayment}
                    setSelectedCreditPayment={setSelectedCreditPayment}
                    insufficientStatus={insufficientStatus}
                    openModal={openModal}
                />
            )}

            {activeTab === 2 && partialBalance === 'loading' && (
                <Image className="d-block mx-auto" src={LoadingCustomSVG} ms={{ width: 40, height: 40 }} />
            )}

            {activeTab === 2 && partialBalance === 'empty' && (
                <Box>
                    <Image src={EmptySVG} className="mx-auto" ms={{ width: 120, height: 120 }} />
                    <Typography className="text-center">گزارشی وجود ندارد</Typography>
                </Box>
            )}

            {activeTab === 2 && Array.isArray(partialBalance) && (
                <PartialBalanceTab
                    GetPartialBalance={partialBalance}
                    selectedCreditPayment={selectedCreditPayment}
                    setSelectedCreditPayment={setSelectedCreditPayment}
                    insufficientStatus={insufficientStatus}
                    openModal={openModal}
                />
            )}
            <Modal className="increaseWalletModal">
                <Modal.Wrapper size="xs">
                    <Modal.Header>افزایش موجودی</Modal.Header>
                    <Typography className="px-lg text-justify">
                        موجودی کیف پول شما کافی نمی باشد. برای تکمیل فرآیند تسویه اعتبار، کیف پول خود را به مبلغ
                        <Typography component="strong">
                            {(Math.floor(+insufficientAmount) / 10).toLocaleString()} تومان
                        </Typography>
                        شارژ نمایید.
                    </Typography>
                    <TextField
                        onChange={setCredit}
                        value={(Math.floor(+insufficientAmount) / 10).toLocaleString()}
                        label="سایر مبالغ (تومان)"
                        hint="حداقل مبلغ 5000 تومان می باشد"
                    />
                    <Box className="d-flex">
                        <Button onClick={chargeWallet}>افزایش موجودی</Button>
                        <Button onClick={() => closeModal('increaseWalletModal')} variant="outlined">
                            انصراف
                        </Button>
                    </Box>
                </Modal.Wrapper>
            </Modal>

            <Modal className="confirmCreditSettlementModal">
                <Modal.Wrapper size="xs">
                    <Modal.Header>تایید تسویه</Modal.Header>
                    <Typography>
                        در صورت تایید مبلغ {(Math.floor(settlementAmount) / 10).toLocaleString()} تومان
                        از موجودی کیف پول شما کسر می گردد.
                    </Typography>
                    <Box className="d-flex" ms={{ marginTop: '20px' }}>
                        <Button onClick={creditSettlement} color="primary">
                            تایید
                        </Button>
                        <Box className="mx-sm" />
                        <Button
                            onClick={() => closeModal('confirmCreditSettlementModal')}
                            variant="outlined"
                            color="danger">
                            انصراف
                        </Button>
                    </Box>
                </Modal.Wrapper>
            </Modal>
        </>
    );
};

export default List;
